import { faChevronDown, faInfoCircle, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck, faFileSpreadsheet } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AlertModal,
  Box,
  Button,
  ConfirmationModal,
  DestructiveModal,
  Divider,
  Flex,
  Heading,
  InlineAlert,
  ListItem,
  Text,
  TextButton,
  TextLink,
  TextWithIcon,
  Toast,
  Tooltip,
  useOpenModal,
} from '@modusign/design-system';
import { iconNeutralSlightlyDark } from '@modusign/design-system/colors';
import { AxiosError } from 'axios';
import { WindowModal, WindowModalContent, WindowModalFooter } from 'components/Modal/WindowModal';
import { exportInputValuesModalMinimize, getExportValuesState } from 'features/exportInputValues';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount, useToggle } from 'react-use';

import LoadingSpinner from '@app/components/LoadingSpinner';
import useExportInputValuesDownloadUrl from '@app/features/exportInputValues/download/hooks/useExportInputValuesDownloadUrl';
import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';
import { ExportInputValuesStatusByJobIdResponse } from '@app/store/transport-layer/document/export-input-values-jobs';

import useAbortExportInputValuesJob from '../../hooks/useAbortExportInputValuesJob';

interface ExportInputValuesProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  progressPercent: number;
  data: ExportInputValuesStatusByJobIdResponse | undefined;
  error: AxiosError | null;
}

export default function ExportInputValuesProgressModal({
  isOpen,
  onClose,
  progressPercent,
  data,
  error,
}: ExportInputValuesProgressModalProps) {
  const { isModalMinimized } = useSelector(getExportValuesState);

  const [isCloseConfirmModalOpen, toggleCloseConfirmModalOpen] = useToggle(false);
  const [isAbortWarningModalOpen, toggleAbortWarningModalOpen] = useToggle(false);
  const [defaultProgressPercent, setDefaultProgressPercent] = useState({
    progressBarGauge: 3,
    progressPercent: 0,
  });

  const dispatch = useDispatch();
  const openModal = useOpenModal();

  useMount(() => {
    // progress modal이 뜬 직후(0.5초 후) 게이지는 3%에서 10%로, 표시 퍼센트는 0%에서 10%로 올려주어야합니다.
    setTimeout(() => setDefaultProgressPercent({ progressBarGauge: 10, progressPercent: 10 }), 500);
  });

  const toggleMinimize = () => dispatch(exportInputValuesModalMinimize({ isMinimized: true }));

  const failureMessage = coalesceGlobalErrorMessage(data?.failureMessage || '', {
    'Job timeout': '추출 제한 시간 만료로 일부 입력값을 추출하지 못했습니다.',
    'Count limit of columns exceeded':
      '추출하려는 입력란이 너무 많아 일부 입력값을 추출하지 못했습니다.',
  });

  const { successDocumentCount, documentCount, failureDocumentCount, status, url } = data ?? {
    successDocumentCount: 0,
    documentCount: 0,
    failureDocumentCount: 0,
    status: 'PENDING',
    url: '',
  };

  const { mutateAsync: abortExportInputValuesJob, isLoading: isAbortExportInputValuesJobLoading } =
    useAbortExportInputValuesJob({
      onSuccess: () => {
        Toast.success({ description: '진행 중인 작업이 취소되었습니다.' });
        onClose();
      },
      onError: (errorMessage) => {
        Toast.error({
          title: '진행 중인 작업을 취소하지 못했습니다.',
          description: `${coalesceGlobalErrorMessage(errorMessage, {
            'Error: Export input values job already completed':
              '이미 추출이 완료되어 다운로드 링크가 메일로 전달되었습니다.',
            'Error: Export input values job already aborted': '이미 취소된 작업입니다.',
          })}`,
        });
      },
    });

  const isJobInProgress = status === 'IN_PROGRESS' || status === 'FAILURE_PROCESSING';
  const isJobFinished = status === 'DONE' || status === 'FAILED';
  const isUnavailableToExport = data?.documentCount === 0 && isJobFinished;

  const { isLoading: isDownloadUrlLoading, refetch: refetchDownloadUrl } =
    useExportInputValuesDownloadUrl({
      jobId: data?.id || '',
      options: {
        enabled: false,
        onError: () =>
          Toast.error({
            title: '파일 다운로드 실패',
            description: '다운로드 링크를 가져오는데 실패했습니다.',
          }),
        onSuccess: ({ url }) => {
          window.location.href = url;
        },
      },
    });

  if (isModalMinimized) {
    return null;
  }

  return (
    <>
      <WindowModal isOpen={isOpen && !isUnavailableToExport} maxWidth="md">
        <Flex
          alignItems="center"
          backgroundColor="lightGray50"
          borderBottom="1px solid"
          borderBottomColor="lightGray300"
          borderTopLeftRadius="3px"
          borderTopRightRadius="3px"
          justifyContent="space-between"
          p={6}
          width="100%"
        >
          <Heading
            as="h1"
            display="flex"
            fontSize="md"
            fontWeight="bold"
            sx={{
              color: 'textNeutralLight',
            }}
          >
            <Flex alignItems="center" height="25px" justifyContent="center" width="25px">
              <FontAwesomeIcon icon={faFileSpreadsheet} />
            </Flex>
            입력값 추출
          </Heading>
          <Flex gap={6}>
            <Tooltip content="작게 보기" placement="top">
              <Box
                aria-label="작게보기"
                as="button"
                color="textNeutralLight"
                fontSize="18px"
                height="26px"
                type="button"
                width="26px"
                onClick={() => toggleMinimize()}
              >
                <FontAwesomeIcon icon={faChevronDown} />
              </Box>
            </Tooltip>

            <Tooltip content="창 닫기" placement="top">
              <Box
                aria-label="창 닫기"
                as="button"
                color="textNeutralLight"
                fontSize="18px"
                height="26px"
                type="button"
                width="26px"
                onClick={isJobFinished ? onClose : toggleCloseConfirmModalOpen}
              >
                <FontAwesomeIcon icon={faXmark} />
              </Box>
            </Tooltip>
          </Flex>
        </Flex>
        <WindowModalContent px={9} py={8}>
          <Flex alignItems="center" justifyContent="space-between" mb={4}>
            {isJobInProgress && progressPercent < 70 && (
              <Box>
                <LoadingSpinner color="black" size={14} />
                <Text as="span" color="textNeutralBase" fontSize="lg" ml={1}>
                  데이터 추출 {progressPercent === 0 && '준비'} 중...
                </Text>
              </Box>
            )}
            {isJobInProgress && progressPercent >= 70 && (
              <Box>
                <LoadingSpinner color="black" size={14} />
                <Text as="span" color="textNeutralLBase" fontSize="lg" ml={1}>
                  csv 파일 생성 중...
                </Text>
              </Box>
            )}
            {isJobFinished && (
              <Text as="span" color="textNeutralLBase" fontSize="lg">
                입력값 추출 파일이 생성되었습니다.
              </Text>
            )}

            {isJobInProgress && (
              <Text fontSize="lg">
                {progressPercent || defaultProgressPercent.progressPercent}%
              </Text>
            )}
            {isJobFinished && (
              <Flex alignItems="center" gap={2}>
                <Text fontSize="lg">완료 {Math.min(documentCount, successDocumentCount)}건 / </Text>
                {failureDocumentCount > 0 && (
                  <Text fontSize="lg">실패 {failureDocumentCount}건 / </Text>
                )}
                <Text fontSize="lg">총 {documentCount}건</Text>
                <TextButton
                  onClick={() =>
                    openModal(({ isOpen, onClose }) => {
                      return (
                        <AlertModal
                          isOpen={isOpen}
                          maxWidth="xs"
                          title="입력값 추출 상세 안내"
                          variant="basic"
                          onClose={onClose}
                        >
                          아래의 문서는 추출에서 제외됩니다.
                          <Box
                            bg="boxBackgroundNeutralBase"
                            borderColor="boxBorderNeutralBase"
                            borderStyle="solid"
                            borderWidth="1px"
                            mt={6}
                            px={6}
                            py={7}
                            textAlign="left"
                          >
                            <ul>
                              <ListItem mb={2} variant="square">
                                문서 잠금이 설정되어 있는 경우
                              </ListItem>

                              <ListItem mb={2} variant="square">
                                전송이 예약된 문서일 경우
                              </ListItem>
                              <ListItem mb={2} variant="square">
                                문서에 대한 접근 권한이 없는 경우
                              </ListItem>
                              <ListItem mb={2} variant="square">
                                요청자가 문서 접근을 차단한 경우
                              </ListItem>
                              <ListItem variant="square">문서가 삭제된 경우</ListItem>
                            </ul>
                          </Box>
                        </AlertModal>
                      );
                    })
                  }
                >
                  상세 안내
                </TextButton>
                <FontAwesomeIcon color="#00A77F" icon={faCircleCheck} size="lg" />
              </Flex>
            )}
          </Flex>

          <Flex bg="lightGray200" borderRadius={100} height={18} width="100%">
            <Box
              bg="statusBackgroundPrimaryBase"
              borderRadius={100}
              height="100%"
              style={{
                width: `${progressPercent || defaultProgressPercent.progressBarGauge}%`,
                transition: 'width 0.5s ease-out',
              }}
            />
          </Flex>

          {failureMessage && isJobFinished && (
            <InlineAlert sx={{ mt: 7 }} variant="error">
              {failureMessage}{' '}
              <TextLink
                href="https://support.modusign.co.kr/hc/ko/articles/6503644465945"
                rel="noopener noreferrer"
                target="_blank"
              >
                자세히
              </TextLink>
            </InlineAlert>
          )}
          <Divider color="light" mt={7} />

          <Box lineHeight={1} pt={7}>
            <Text color="textNeutralBase" fontSize="md" fontWeight="medium" mb={1}>
              얼마나 걸리나요? <br />
            </Text>
            <Text color="textNeutralLight" fontSize="sm">
              네트워크 환경, 추출 데이터량에 따라 10분에서 최대 24시간까지도 걸릴 수 있어요.
            </Text>
          </Box>

          <Box lineHeight={1} pt={7}>
            <Text
              color="textNeutralBase"
              fontSize="md"
              fontWeight="medium"
              mb={1}
              textAlign="center"
            >
              완료되면 이메일로 보내드릴게요. <br />
            </Text>
            <Text color="textNeutralLight" fontSize="sm">
              창을 닫더라도 추출은 계속 진행됩니다. 완료되면 이메일로 다운로드 링크가 전송됩니다.
            </Text>
          </Box>
          <TextWithIcon
            color="textNeutralSlightlyLight"
            iconContainerWidth="12px"
            leftIcon={<FontAwesomeIcon color={iconNeutralSlightlyDark} icon={faInfoCircle} />}
            mb={2}
            mt={7}
          >
            추출이 끝난 파일은 보안을 위해 비밀번호를 설정하여 보관해 주세요.
          </TextWithIcon>
        </WindowModalContent>
        <WindowModalFooter display="flex" justifyContent="flex-end">
          {!isJobFinished && (
            <Button
              ml={3}
              variant="basic"
              onClick={() => {
                toggleAbortWarningModalOpen();
              }}
            >
              추출 취소
            </Button>
          )}
          <Tooltip
            content="추출이 완료될 때까지 기다려주세요."
            disabled={isJobFinished}
            isOpen={!isJobInProgress ? false : undefined}
            placement="top"
          >
            <Button
              disabled={!url}
              isLoading={isDownloadUrlLoading}
              ml={3}
              mr={4}
              variant="primary"
              onClick={() => {
                refetchDownloadUrl();
              }}
            >
              파일 다운로드
            </Button>
          </Tooltip>
        </WindowModalFooter>
      </WindowModal>

      <AlertModal
        isOpen={!!error}
        title="입력값 추출 중"
        variant="warning"
        onClose={() => {
          onClose();
        }}
      >
        {coalesceGlobalErrorMessage(error?.message || '', {
          'Export input values job does not exist': '입력값 추출 진행상태를 조회할 수 없습니다.',
          'Forbidden user': '요청하신 입력값 추출 조회 권한이 없습니다.',
        })}
      </AlertModal>

      <ConfirmationModal
        closeButtonLabel="취소"
        confirmButtonLabel="추출 창 닫기"
        isOpen={isCloseConfirmModalOpen}
        maxWidth="xs"
        title="입력값 추출 안내"
        variant="warning"
        onClose={() => toggleCloseConfirmModalOpen()}
        onConfirm={onClose}
      >
        <Box textAlign="center">
          <Text color="textNeutralBase" fontWeight="medium" mb={1}>
            입력값 추출 창을 닫을까요?
          </Text>
          <br />
          <Text color="textNeutralBase" fontWeight="regular" sx={{ wordBreak: 'keep-all' }}>
            창을 닫으면 계정 이메일로 전달된 파일 다운로드 링크에서만 입력값 추출 파일을 받을 수
            있습니다.
          </Text>
        </Box>
      </ConfirmationModal>

      <AlertModal
        // 작업이 끝났는데 총 문서 수가 0이면, 추출 가능한 문서가 없는 경우로 안내 모달을 띄웁니다.
        isOpen={isUnavailableToExport}
        maxWidth="xs"
        title="추출할 수 없는 문서"
        variant="warning"
        onClose={onClose}
      >
        아래 항목에 해당되는 문서는 입력값 추출에서 제외됩니다.
        <Box
          bg="boxBackgroundNeutralBase"
          borderColor="boxBorderNeutralBase"
          borderStyle="solid"
          borderWidth="1px"
          mt={6}
          px={6}
          py={7}
          textAlign="left"
        >
          <ul>
            <ListItem mb={2} variant="square">
              문서 잠금이 설정되어 있는 경우
            </ListItem>

            <ListItem mb={2} variant="square">
              전송이 예약된 문서일 경우
            </ListItem>
            <ListItem mb={2} variant="square">
              문서에 대한 접근 권한이 없는 경우
            </ListItem>
            <ListItem mb={2} variant="square">
              요청자가 문서 접근을 차단한 경우
            </ListItem>
            <ListItem variant="square">문서가 삭제된 경우</ListItem>
          </ul>
        </Box>
      </AlertModal>

      <DestructiveModal
        closeButtonLabel="닫기"
        confirmButtonLabel="취소하기"
        isLoading={isAbortExportInputValuesJobLoading}
        isOpen={isAbortWarningModalOpen && isJobInProgress}
        maxWidth="xxs"
        title="입력값 추출 취소"
        onClose={() => {
          toggleAbortWarningModalOpen();
        }}
        onConfirm={() => {
          abortExportInputValuesJob(data?.id || '').then(toggleAbortWarningModalOpen);
        }}
      >
        <Text color="textNegativeBase" fontSize="md" fontWeight="medium" mb={1} textAlign="center">
          진행 중인 추출 작업을 취소할까요?
        </Text>
        <Text fontSize="md" textAlign="center">
          이미 추출이 완료된 경우, 파일 다운로드 링크가 이메일로 발송될 수 있습니다.
        </Text>
      </DestructiveModal>
    </>
  );
}
